import { DestroyRef as SearchDestroyRef, inject as searchInject } from '@angular/core';
import { bindSearchModeForm } from '../../models/search-mode-form';
import { selectRealEstateState } from '../../store/selectors/real-estate.selectors';
import { ensureRealEstateList } from '../../store/actions/real-estate.actions';
import {
  resetRealEstateSearch,
  retryRealEstateSearch,
} from '../../store/actions/real-estate.actions';
import { withoutViewport } from '../../models/search-request';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { RealEstate } from '../../models/real-estate.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { filter, map, Observable, Subject, take, takeUntil } from 'rxjs';
import { selectIsArabic } from '../../store/selectors/language.selectors';
import { AdCategories } from '../../models/ad-categories.model';
import {
  selectIsRealEstateMapView,
  selectRealEstateSearchForm,
  selectRealEstateSearchLoading,
  selectRealEstateSearchResults,
} from '../../store/selectors/real-estate.selectors';
import { Roles } from '../../shared/constants/role.constants';
import { Store } from '@ngrx/store';
import {
  selectCategories,
  selectCategoriesLoading,
} from '../../store/selectors/categories.selectors';
import { loadCategories } from '../../store/actions/categories.actions';
import {
  searchMapRealEstates,
  searchRealEstates,
  updateSearchForm,
} from '../../store/actions/real-estate.actions';
import { RealEstateSearch } from '../../models/real-estate-search.model';
import { Location } from '@angular/common';
import { MapParameters } from '../../models/map-parameters.model';
import { toSignal } from '@angular/core/rxjs-interop';

@Component({
  selector: 'app-property-search',
  standalone: false,
  templateUrl: './property-search.component.html',
  styleUrl: './property-search.component.scss',
  encapsulation: ViewEncapsulation.None,
})
export class PropertySearchComponent implements OnInit, OnDestroy {
  private readonly searchDestroyRef = searchInject(SearchDestroyRef);
  searchForm!: FormGroup;
  properties: RealEstate[] = [];
  loadingResult = false;
  advancedVisible = false;
  maxYear = new Date().getFullYear() + 1;

  isArabic$: Observable<boolean> = this.store.select(selectIsArabic);

  categories$!: Observable<AdCategories | null>;
  loadingCategories$!: Observable<boolean>;

  searchResults$ = this.store.select(selectRealEstateSearchResults);
  loadingResult$ = this.store.select(selectRealEstateSearchLoading);
  isMapView = toSignal(this.store.select(selectIsRealEstateMapView));

  mapParameters?: MapParameters;

  roles = Roles;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private store: Store,
    private location: Location
  ) {
    window.scrollTo(0, 0);
    this.categories$ = this.store.select(selectCategories).pipe(
      filter((res) => !!res),
      map((res) => {
        return {
          ...res,
          statuses: [...res?.statuses, { code: 'EXPIRED', description: 'EXPIRED' }],
        } as AdCategories;
      })
    );
    this.loadingCategories$ = this.store.select(selectCategoriesLoading);

    this.store.dispatch(loadCategories());
    this.store.dispatch(ensureRealEstateList());

  }

  ngOnInit(): void {
    this.searchForm = this.fb.group({
      propertyTypes: [],
      typeOfPossessions: [],
      isForRent: undefined,
      yearFrom: undefined,
      yearTo: undefined,
      isInstallmentAvailable: this.fb.control<boolean | null>(null),
      onlyWithPrice: this.fb.control(false, { nonNullable: true }),
      priceFrom: undefined,
      priceTo: undefined,
      floorAreaFrom: undefined,
      floorAreaTo: undefined,
      landAreaFrom: undefined,
      landAreaTo: undefined,
      floorNumberFrom: undefined,
      floorNumberTo: undefined,
      numberOfFloorsFrom: undefined,
      numberOfFloorsTo: undefined,
      roomsFrom: undefined,
      roomsTo: undefined,
      bedroomsFrom: undefined,
      bedroomsTo: undefined,
      bathroomsFrom: undefined,
      bathroomsTo: undefined,
      conditions: [],
      hasBalcony: undefined,
      hasGarden: undefined,
      hasPool: undefined,
      hasParking: undefined,
      hasLift: undefined,
      hasSolarEnergy: undefined,
      isFurnished: undefined,
      statuses: [],
      northEastLat: undefined,
      northEastLng: undefined,
      southWestLat: undefined,
      southWestLng: undefined,
    });

    bindSearchModeForm(this.searchForm, this.store.select(selectRealEstateState),
      state => state.isRealEstateMapView, form => this.store.dispatch(updateSearchForm({form})), this.searchDestroyRef);
  }

  onSearch() {
    const searchParams = (this.searchForm?.value as RealEstateSearch) ?? ({} as RealEstateSearch);
    this.store.dispatch(updateSearchForm({ form: searchParams }));
    this.callSearch(searchParams);
  }

  callSearch(searchParams: RealEstateSearch) {
    const isMapView = this.isMapView();

    if (isMapView) {
      this.store.dispatch(searchMapRealEstates({ mapQuery: { ...searchParams, isMapView } }));
    } else {
      this.clearMapData();
      this.store.dispatch(searchRealEstates({ query: withoutViewport(searchParams) }));
    }
  }

  reset() {
    this.searchForm.reset({ onlyWithPrice: false });
    if (this.isMapView()) this.mapParameters = undefined;
    this.clearMapData();
    this.store.dispatch(resetRealEstateSearch());
  }

  toggleAdvanced(): void {
    this.advancedVisible = !this.advancedVisible;
    if (!this.advancedVisible) {
      window.scrollTo(0, 0);
    }
  }

  toggleIsRent(value: boolean): void {
    const isForRent = this.searchForm.get('isForRent');
    if (
      (isForRent?.value === true && value === true) ||
      (isForRent?.value === false && value === false)
    ) {
      isForRent?.setValue(undefined);
    } else {
      isForRent?.setValue(value);
    }
  }

  goBack() {
    this.location.back();
  }

  mapChanged(event: MapParameters) {
    this.mapParameters = event;
    this.northEastLat?.setValue(event.northEastLat);
    this.northEastLng?.setValue(event.northEastLng);
    this.southWestLat?.setValue(event.southWestLat);
    this.southWestLng?.setValue(event.southWestLng);
    this.onSearch();
  }

  clearMapData() {
    this.northEastLat?.setValue(undefined);
    this.northEastLng?.setValue(undefined);
    this.southWestLat?.setValue(undefined);
    this.southWestLng?.setValue(undefined);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get usedCriteriaCount(): number {
    return Object.entries(this.searchForm.value).filter(([key, v]) => key === 'onlyWithPrice' ? v === true : this.isUsed(v)).length;
  }

  isUsed(value: any): boolean {
    if (typeof value === 'boolean') {
      return value !== undefined;
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    if (typeof value === 'object' && value !== null) {
      return Object.values(value).some((v) => this.isUsed(v));
    }

    return value !== null && value !== undefined && value !== '' && !/^\d+\.\d+/.test(value);
  }

  get northEastLat() {
    return this.searchForm.get('northEastLat');
  }
  get northEastLng() {
    return this.searchForm.get('northEastLng');
  }
  get southWestLat() {
    return this.searchForm.get('southWestLat');
  }
  get southWestLng() {
    return this.searchForm.get('southWestLng');
  }
}
